import React from 'react';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { CalendarPlus } from 'lucide-react';

const formatICSDate = (date) => {
    const d = new Date(date);
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    const hours = String(d.getUTCHours()).padStart(2, '0');
    const minutes = String(d.getUTCMinutes()).padStart(2, '0');
    const seconds = String(d.getUTCSeconds()).padStart(2, '0');
    return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
};

const formatGoogleCalendarDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}${month}${day}T${hours}${minutes}${seconds}`;
};

export default function AddToCalendarButton({ appointment, sx }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const addToAppleCalendar = () => {
        if (!appointment) return;

        const startDate = new Date(appointment.appointment_time);
        const endDate = new Date(startDate.getTime() + 15 * 60 * 1000); // Add 15 minutes

        const icsContent = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//Piction Health//Appointment//EN',
            'BEGIN:VEVENT',
            `UID:${appointment.id}@pictionhealth.com`,
            `DTSTART:${formatICSDate(startDate)}`,
            `DTEND:${formatICSDate(endDate)}`,
            `SUMMARY:Video Visit - ${appointment.patientName}`,
            `DESCRIPTION:Video Visit appointment with ${appointment.patientName}${appointment.video_link ? `\\n\\nJoin: ${appointment.video_link}` : ''}`,
            appointment.video_link ? `URL:${appointment.video_link}` : '',
            'END:VEVENT',
            'END:VCALENDAR'
        ].filter(line => line !== '').join('\r\n');

        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `appointment-${appointment.id}.ics`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        handleClose();
    };

    const addToGoogleCalendar = () => {
        if (!appointment) return;

        const startDate = new Date(appointment.appointment_time);
        const endDate = new Date(startDate.getTime() + 15 * 60 * 1000); // Add 15 minutes

        const title = encodeURIComponent(`Video Visit - ${appointment.patientName}`);
        const start = formatGoogleCalendarDate(startDate);
        const end = formatGoogleCalendarDate(endDate);
        const description = encodeURIComponent(
            `Video Visit appointment with ${appointment.patientName}${appointment.video_link ? `\n\nJoin: ${appointment.video_link}` : ''}`
        );
        const location = appointment.video_link ? encodeURIComponent(appointment.video_link) : '';

        const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${description}${location ? `&location=${location}` : ''}`;

        window.open(googleCalendarUrl, '_blank', 'noopener,noreferrer');
        handleClose();
    };

    return (
        <>
            <Button
                variant="outlined"
                startIcon={<CalendarPlus size={14} color="#2e8da6" />}
                onClick={handleClick}
                sx={{
                    color: '#2e8da6',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.65rem',
                    py: 0.5,
                    px: 1,
                    whiteSpace: 'nowrap',
                    borderRadius: '10px',
                    border: '1px solid #2e8da6 !important',
                    backgroundColor: 'transparent',
                    '& .MuiButton-startIcon': {
                        marginRight: '4px',
                        marginLeft: 0,
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(46, 141, 166, 0.1)',
                        border: '1px solid #2e8da6 !important',
                    },
                    ...sx,
                }}
            >
                Add to Calendar
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            >
                <MenuItem onClick={addToAppleCalendar}>
                    <ListItemIcon>
                        <CalendarPlus size={18} color="#2e8da6" />
                    </ListItemIcon>
                    <ListItemText primary="Apple Calendar" />
                </MenuItem>
                <MenuItem onClick={addToGoogleCalendar}>
                    <ListItemIcon>
                        <CalendarPlus size={18} color="#2e8da6" />
                    </ListItemIcon>
                    <ListItemText primary="Google Calendar" />
                </MenuItem>
            </Menu>
        </>
    );
}
